import React, { useEffect, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { SwapOutlined } from "@ant-design/icons";

import { CompassModal } from "../../../components/common/Modal";
import {
  darkGreen,
  greyText,
  lightGreyText,
  lightGreyOutline,
  compassBordeaux,
  compassLightGreen,
  compassYellow,
  ESCALATION_COLORS,
  AI_GRADIENT,
} from "../../../styles";
import { EASE } from "../../../components/visuals/motion";
import {
  Chip,
  Chips,
  DetailSection,
  DetailSectionLabel,
  DetailText,
} from "../../../components/visuals/DetailAtoms";
import {
  ESCALATION_MAX,
  EscalationAvatar,
  EscalationMeter,
} from "../../../components/visuals/Escalation";
import {
  SIGNAL_ICON_PX,
  SignalIconSize,
  SignalTabItem,
  SignalTabs,
  avatarIcon,
} from "../../../components/visuals/SignalTabs";
import {
  VoteDecisionMark,
  toVoteDecision,
} from "../../../components/visuals/VoteDecisionMark";
import { MESSAGES } from "../../../messages";
import {
  EscalationParent,
  formatEscalationCode,
  getEscalationLevel,
} from "../../../enums/Escalations";
import {
  INTERACTION_CLASS_DISPLAY,
  LINKAGE_TYPE_DISPLAY,
  SEQUENCE_FLOW_DISPLAY,
} from "../../../models/engagementVotingMappingModels";
import { Momentum, MomentumDisplay } from "../../../models/stewardshipModels";
import { getSdgIcon } from "../../../utils/formattingUtils";
import { resolveDisplayName } from "../../../constants";
import { Avatar } from "../../../components/visuals/Avatar";
import { ScoreRing } from "../../SingleEngagement/AssessmentView/v2/ScoreRing";
import {
  ActivityType,
  VoteActivity,
  InteractionActivity,
  getInteractionClassImage,
  getLinkageTypeImage,
  getSequenceFlowImage,
} from "../utils";

import interactionIcon from "../../../assets/svg/interaction_grey.svg";
import votingIcon from "../../../assets/svg/vote_grey.svg";
import aiLogo from "../../../assets/svg/ai_logo.svg";

dayjs.extend(relativeTime);

const drawLine = keyframes`
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
`;

const flowLine = keyframes`
  from { background-position: 0% 50%; }
  to { background-position: 300% 50%; }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 28px 24px;
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 10px;
  padding: 12px 0;
  font-family: "Apercu";
  font-size: 12px;
  color: ${greyText};
`;

const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
`;

const Sep = styled.span`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: ${lightGreyOutline};
`;

const Muted = styled.span`
  color: ${lightGreyText};
`;

const Spacer = styled.span`
  flex: 1;
`;

const Dot = styled.span<{ $tint: string }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $tint }): string => $tint};
`;

const AiTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: "ApercuMedium";
  font-size: 11.5px;
  white-space: nowrap;

  > span:last-child {
    background: ${AI_GRADIENT};
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
  }
`;

const Verdict = styled.div<{ $ai?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 4px 0 18px;
  min-width: 0;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 1px;
    transform-origin: left;
    ${({ $ai }): ReturnType<typeof css> =>
      $ai
        ? css`
            background: linear-gradient(
              90deg,
              #6ef2a6,
              #32e0c4,
              #2bb3f3,
              #3a5cf5,
              #2bb3f3,
              #32e0c4,
              #6ef2a6
            );
            background-size: 300% 100%;
            animation:
              ${drawLine} 0.8s ${EASE} 0.2s both,
              ${flowLine} 6s linear infinite;
          `
        : css`
            background: ${lightGreyOutline};
            animation: ${drawLine} 0.6s ${EASE} 0.1s both;
          `}
  }

  @media (prefers-reduced-motion: reduce) {
    &::after {
      animation: none;
    }
  }
`;

const VerdictText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const Headline = styled.span`
  font-family: "ApercuMedium";
  font-size: 16px;
  color: ${darkGreen};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Subline = styled.span`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 8px;
  font-family: "Apercu";
  font-size: 12px;
  color: ${greyText};
`;

const VerdictNumber = styled.span`
  font-family: "ApercuMedium";
  font-size: 11px;
`;

const EscalationNumber = styled(VerdictNumber)`
  color: ${darkGreen};
`;

const Code = styled.span`
  font-family: "ApercuMono", "Apercu", monospace;
  font-size: 11px;
  color: ${lightGreyText};
`;

const MOMENTUM_TINT: Record<Momentum, string> = {
  [Momentum.PROGRESSING]: compassLightGreen,
  [Momentum.STALLING]: compassYellow,
  [Momentum.UNKNOWN]: lightGreyText,
};

const SDG_NAMES: Record<number, string> =
  MESSAGES.STEWARDSHIP.ENGAGEMENT_MODULE.SDG_NAMES;

const ESCALATION_SHORT: Record<string, string> =
  MESSAGES.STEWARDSHIP.ENGAGEMENT_MODULE.ESCALATION_SHORT;

const ESCALATION_FULL: Record<string, string> = Object.values(
  MESSAGES.STEWARDSHIP.ENGAGEMENT_MODULE.ESCALATION_MESSAGES,
).reduce<Record<string, string>>((acc, group) => ({ ...acc, ...group }), {});

const ESCALATION_PARENT: Record<number, EscalationParent> = {
  1: EscalationParent.E1,
  2: EscalationParent.E2,
  3: EscalationParent.E3,
  4: EscalationParent.E4,
  5: EscalationParent.E5,
};

const parseSdgNumber = (sdg: string): number => {
  const match = sdg.match(/\d+/);
  return match ? parseInt(match[0], 10) : 0;
};

const escalationName = (key: string): string =>
  ESCALATION_SHORT[key.toUpperCase()] ?? formatEscalationCode(key);

interface LinkStrength {
  label: string;
  tint: string;
}

const linkStrength = (pct: number): LinkStrength => {
  if (pct >= 85) return { label: "Strong", tint: compassLightGreen };
  if (pct >= 65) return { label: "Moderate", tint: compassYellow };
  if (pct >= 40) return { label: "Weak", tint: compassYellow };
  return { label: "Tenuous", tint: compassBordeaux };
};

interface VoteDecision {
  vote: string;
  management: string;
}

const parseVoteDecision = (details?: string): VoteDecision | null => {
  const match = details?.match(/Vote:\s*([^|]+)\|\s*Mgmt:\s*(.+)/i);
  if (!match) return null;
  return { vote: match[1].trim(), management: match[2].trim() };
};

const useCountUp = (target: number, ms = 900): number => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setValue(target);
      return undefined;
    }
    let frame = 0;
    const start = performance.now();
    const tick = (now: number): void => {
      const t = Math.min(1, (now - start) / ms);
      setValue(Math.round(target * (1 - Math.pow(1 - t, 3))));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return (): void => cancelAnimationFrame(frame);
  }, [target, ms]);
  return value;
};

const DateMeta = ({ date }: { date: string }): JSX.Element | null => {
  const d = dayjs(date);
  return d.isValid() ? (
    <MetaItem>
      {d.format("D MMM YYYY")}
      <Muted>{d.fromNow()}</Muted>
    </MetaItem>
  ) : null;
};

interface ManagerProps {
  managerName?: string | null;
  managerImage?: string | null;
}

const VoteView = ({
  activity,
  managerName,
  managerImage,
}: { activity: VoteActivity } & ManagerProps): JSX.Element => {
  const mapping = activity.voting_mapping;
  const pct = Math.round(mapping.confidence_score * 100);
  const strength = linkStrength(pct);
  const shownPct = useCountUp(pct);
  const decision = parseVoteDecision(activity.details);
  const factors = (mapping.matching_factors ?? "")
    .split("|")
    .map((f) => f.trim())
    .filter(Boolean);

  const interaction = INTERACTION_CLASS_DISPLAY[mapping.interaction_class];
  const linkage = LINKAGE_TYPE_DISPLAY[mapping.linkage_type];
  const sequence = SEQUENCE_FLOW_DISPLAY[mapping.sequence_flow];

  const items: SignalTabItem[] = [
    {
      key: "interaction",
      label: interaction,
      kind: "Interaction class",
      renderIcon: avatarIcon(
        interaction,
        getInteractionClassImage(mapping.interaction_class),
      ),
      detail: <DetailText>{mapping.interaction_class_rationale}</DetailText>,
    },
    {
      key: "linkage",
      label: linkage,
      kind: "Linkage type",
      renderIcon: avatarIcon(linkage, getLinkageTypeImage(mapping.linkage_type)),
      detail: <DetailText>{mapping.linkage_type_rationale}</DetailText>,
    },
    {
      key: "sequence",
      label: sequence,
      kind: "Sequence",
      renderIcon: avatarIcon(
        sequence,
        getSequenceFlowImage(mapping.sequence_flow),
      ),
      detail: <DetailText>{mapping.sequence_flow_rationale}</DetailText>,
    },
  ];

  return (
    <Body>
      <Meta>
        <DateMeta date={activity.date} />
        {managerName && (
          <>
            <Sep />
            <MetaItem>
              <Avatar
                username={managerName}
                image={managerImage || undefined}
                size="xxsmall"
              />
              {managerName}
            </MetaItem>
          </>
        )}
        {mapping.is_cross_manager && (
          <>
            <Sep />
            <MetaItem>
              <SwapOutlined />
              Cross-manager
            </MetaItem>
          </>
        )}
        <Spacer />
        <AiTag>
          <Avatar username="Compass AI" image={aiLogo} size="xxsmall" />
          <span>Compass AI</span>
        </AiTag>
      </Meta>

      <Verdict $ai>
        <ScoreRing
          value={pct}
          tint={strength.tint}
          glyph={<VerdictNumber>{shownPct}</VerdictNumber>}
          size="lg"
          tooltip={`Link confidence · ${pct}%`}
        />
        <VerdictText>
          <Headline>{strength.label} link to the engagement</Headline>
          {decision && (
            <Subline>
              <MetaItem>
                <VoteDecisionMark decision={toVoteDecision(decision.vote)} />
                Voted {decision.vote.toLowerCase()}
              </MetaItem>
              <Sep />
              <Muted>Management {decision.management.toLowerCase()}</Muted>
            </Subline>
          )}
        </VerdictText>
      </Verdict>

      {(mapping.rationale || factors.length > 0) && (
        <DetailSection>
          <DetailSectionLabel>Summary</DetailSectionLabel>
          {mapping.rationale && <DetailText>{mapping.rationale}</DetailText>}
          {factors.length > 0 && (
            <Chips>
              {factors.map((factor) => (
                <Chip key={factor}>{factor}</Chip>
              ))}
            </Chips>
          )}
        </DetailSection>
      )}

      <SignalTabs items={items} />
    </Body>
  );
};

const InteractionView = ({
  activity,
}: {
  activity: InteractionActivity;
}): JSX.Element => {
  const sdgs = activity.sdgs ?? [];
  const escalations = activity.escalations ?? [];
  const top = escalations.reduce<string | null>(
    (best, esc) =>
      best === null || getEscalationLevel(esc) > getEscalationLevel(best)
        ? esc
        : best,
    null,
  );
  const peak = top ? getEscalationLevel(top) : 0;

  const escalationItems: SignalTabItem[] = escalations.map((esc) => {
    const level = getEscalationLevel(esc);
    return {
      key: `esc-${esc}`,
      label: escalationName(esc),
      kind: `Escalation · ${ESCALATION_PARENT[level] ?? `Level ${level}`}`,
      renderIcon: (size: SignalIconSize): JSX.Element => (
        <EscalationAvatar code={esc} size={SIGNAL_ICON_PX[size]} />
      ),
      detail: (
        <>
          {ESCALATION_FULL[esc.toUpperCase()] && (
            <DetailText>{ESCALATION_FULL[esc.toUpperCase()]}</DetailText>
          )}
          <Subline>
            <EscalationMeter level={level} />
            Level {level} of {ESCALATION_MAX}
            <Sep />
            <Code>{formatEscalationCode(esc)}</Code>
          </Subline>
        </>
      ),
    };
  });

  const sdgItems: SignalTabItem[] = sdgs.map((sdg) => {
    const n = parseSdgNumber(sdg);
    return {
      key: `sdg-${sdg}`,
      label: SDG_NAMES[n] ?? `SDG ${n}`,
      kind: `Sustainable Development Goal ${n}`,
      renderIcon: avatarIcon(sdg, getSdgIcon(n)),
      detail: (
        <DetailText>
          Tagged against UN SDG {n}
          {SDG_NAMES[n] ? ` — ${SDG_NAMES[n]}` : ""}.
        </DetailText>
      ),
    };
  });

  const items = [...escalationItems, ...sdgItems];

  return (
    <Body>
      <Meta>
        <DateMeta date={activity.date} />
        {activity.created_by && (
          <>
            <Sep />
            <MetaItem>
              <Avatar username={activity.created_by} size="xxsmall" />
              {resolveDisplayName(activity.created_by)}
            </MetaItem>
          </>
        )}
      </Meta>

      <Verdict>
        <ScoreRing
          value={(peak / ESCALATION_MAX) * 100}
          tint={peak > 0 ? ESCALATION_COLORS[peak] : lightGreyText}
          glyph={
            <EscalationNumber>{peak > 0 ? `E${peak}` : "–"}</EscalationNumber>
          }
          size="lg"
          tooltip={`Escalation level ${peak} of ${ESCALATION_MAX}`}
        />
        <VerdictText>
          <Headline>{top ? escalationName(top) : "No escalation logged"}</Headline>
          <Subline>
            {activity.momentum !== undefined ? (
              <MetaItem>
                <Dot $tint={MOMENTUM_TINT[activity.momentum]} />
                {MomentumDisplay[activity.momentum]}
              </MetaItem>
            ) : (
              <Muted>No momentum set</Muted>
            )}
            <Sep />
            <MetaItem>
              <EscalationMeter level={peak} />
              Level {peak} of {ESCALATION_MAX}
            </MetaItem>
          </Subline>
        </VerdictText>
      </Verdict>

      {activity.details && (
        <DetailSection>
          <DetailSectionLabel>Notes</DetailSectionLabel>
          <DetailText>{activity.details}</DetailText>
        </DetailSection>
      )}

      <SignalTabs items={items} />
    </Body>
  );
};

interface ActivityDetailModalProps extends ManagerProps {
  show: boolean;
  onClose: () => void;
  activity: VoteActivity | InteractionActivity | null;
}

export const ActivityDetailModal: React.FC<ActivityDetailModalProps> = ({
  show,
  onClose,
  activity,
  managerName,
  managerImage,
}) => {
  if (!activity) return null;

  const isVote = activity.type === ActivityType.VOTE;
  const title = isVote
    ? activity.description || "Voting record"
    : activity.interactionTitle || activity.description || "Interaction";

  return (
    <CompassModal
      show={show}
      handleClose={onClose}
      size="lg"
      title={title}
      titleIcon={isVote ? votingIcon : interactionIcon}
      maxHeight={720}
    >
      {activity.type === ActivityType.VOTE ? (
        <VoteView
          key={activity.id}
          activity={activity}
          managerName={managerName}
          managerImage={managerImage}
        />
      ) : (
        <InteractionView key={activity.id} activity={activity} />
      )}
    </CompassModal>
  );
};
